import { omtalte, type InnleggDto, type SakDto } from '@es/shared';

/** Detaljsiden for en sak. Se `domain/sak.ts`. */

/** «KN» for Kari Nordmann, «E» for Eirik. Til avatarene. */
export function forbokstaver(navn: string): string {
  const deler = navn
    .trim()
    .split(/\s+/)
    .filter((d) => d !== '');
  const forste = deler[0]?.charAt(0) ?? '?';
  const siste = deler.length > 1 ? (deler.at(-1)?.charAt(0) ?? '') : '';
  return (forste + siste).toUpperCase();
}

/**
 * En farge per person, så samme navn alltid har samme avatar. Bare de fire
 * mørkeste seriefargene: hvit tekst på de lyseste når ikke kontrastkravet.
 */
export function avatarfarge(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 4;
  return h + 1;
}

/**
 * Omtalen som skrives akkurat nå: teksten etter den siste «@» før markøren,
 * så lenge den ikke inneholder linjeskift. Null når det ikke skrives en.
 */
export function aktivOmtale(tekst: string, markor: number): { start: number; sok: string } | null {
  const for_ = tekst.slice(0, markor);
  const at = for_.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(for_.charAt(at - 1))) return null;
  const sok = for_.slice(at + 1);
  if (sok.includes('\n') || sok.length > 40) return null;
  return { start: at, sok };
}

/** Personene som passer det som er skrevet etter «@», høyst seks. */
export function forslag<T extends { readonly navn: string }>(
  personer: readonly T[],
  sok: string,
): T[] {
  const s = sok.toLowerCase();
  return personer
    .filter(
      (p) =>
        p.navn
          .toLowerCase()
          .split(/\s+/)
          .some((d) => d.startsWith(s)) || p.navn.toLowerCase().startsWith(s),
    )
    .slice(0, 6);
}

/** Setter inn «@Navn » der omtalen startet, og gir teksten og ny markørposisjon. */
export function settInnOmtale(
  tekst: string,
  omtale: { start: number; sok: string },
  navn: string,
): { tekst: string; markor: number } {
  const ut = `${tekst.slice(0, omtale.start)}@${navn} ${tekst.slice(omtale.start + 1 + omtale.sok.length)}`;
  return { tekst: ut, markor: omtale.start + navn.length + 2 };
}

/** Id-ene til dem som er omtalt i teksten, blant dem som kan omtales. */
export function omtalteIder(tekst: string, personer: SakDto['personer']): string[] {
  return omtalte(tekst, personer).map((p) => p.id);
}

/** Hva som står ved navnet i tidslinjen. */
export function handlingstekst(i: InnleggDto, vedlegg: number): string {
  if (i.type === 'hendelse') return 'oppdaterte saken';
  if (i.tekst === '' && vedlegg > 0)
    return vedlegg === 1 ? 'delte en fil' : `delte ${String(vedlegg)} filer`;
  return i.omtalte.length > 0
    ? `kommenterte og nevnte ${i.omtalte.map((p) => p.navn).join(', ')}`
    : 'kommenterte';
}

const TIDSPUNKT = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Oslo',
});

/** «12. okt. 2026, 14:34». */
export function tidspunkt(iso: string): string {
  return TIDSPUNKT.format(new Date(iso));
}

const DATO = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** «10. okt. 2026», for en dato uten klokkeslett. */
export function datotekst(iso: string): string {
  return DATO.format(new Date(`${iso}T00:00:00Z`));
}

/** Etikettene fra et tekstfelt: skilt med komma. */
export function lesEtiketter(tekst: string): string[] {
  return tekst
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e !== '');
}

/** «1:30» eller «90» eller «1,5» til minutter. Null når det ikke er en varighet. */
export function lesVarighet(tekst: string): number | null {
  const t = tekst.trim().replace(/\s/g, '');
  const timer = /^(\d{1,2}):([0-5]\d)$/.exec(t);
  if (timer) return Number(timer[1]) * 60 + Number(timer[2]);
  const desimal = /^(\d{1,2})[.,](\d{1,2})$/.exec(t);
  if (desimal) return Math.round(Number(`${desimal[1] ?? '0'}.${desimal[2] ?? '0'}`) * 60);
  if (/^\d{1,4}$/.test(t)) return Number(t);
  return null;
}
